<div class="page-head">
  <div>
    <p class="eyebrow">งานอนุมัติ</p>
    <h1>อนุมัติการปรับวงเงิน</h1>
    <p>ตรวจสอบการเพิ่มวงเงินค่าใช้จ่าย ยอดเรียกเก็บ และเงินสำรองบริษัท</p>
  </div>
</div>

<section class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
  <p-table
    #adjustmentTable
    [value]="rows()"
    dataKey="id"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 25, 50]"
    [globalFilterFields]="[
      'adjustmentNo',
      'booking.bookingNo',
      'booking.booking_customer.name',
      'booking.tourName',
      'tour.tourNo',
      'tour.tourName',
      'requestedBy.firstName',
      'requestedBy.lastName',
      'reason',
      'status',
    ]"
    [rowHover]="true"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="แสดง {first} ถึง {last} จาก {totalRecords} รายการ"
    [tableStyle]="{ 'min-width': '1120px' }"
  >
    <ng-template #caption>
      <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <label class="relative block w-full max-w-md">
          <lucide-icon
            name="Search"
            [size]="17"
            class="pointer-events-none absolute top-1/2 left-2 z-10 -translate-y-1/2 text-slate-400"
          />
          <input
            pInputText
            #query
            class="h-11 w-full pr-3 text-sm placeholder:text-slate-400"
            style="padding-left: 2.75rem; border-radius: 0.5rem"
            placeholder="ค้นหาคำขอ Booking ลูกค้า ผู้สร้าง หรือเหตุผล"
            (input)="adjustmentTable.filterGlobal(query.value, 'contains')"
          />
        </label>
        <div class="flex items-center justify-end gap-3">
          <p class="m-0 text-xs text-slate-500">
            รออนุมัติทั้งหมด
            <span class="font-semibold text-slate-700">{{ rows().length }}</span>
            รายการ
          </p>
          <app-refresh-button
            label="รีเฟรชรายการอนุมัติการปรับวงเงิน"
            [loading]="isLoadingAdjustments()"
            (refresh)="loadAdjustments()"
          />
        </div>
      </div>
    </ng-template>

    <ng-template #header>
      <tr>
        <th class="w-14 text-center text-xs whitespace-nowrap">ลำดับ</th>
        <th pSortableColumn="adjustmentNo" class="min-w-56 text-xs whitespace-nowrap">
          คำขอปรับวงเงิน <p-sortIcon field="adjustmentNo" />
        </th>
        <th pSortableColumn="booking.bookingNo" class="min-w-72 text-xs whitespace-nowrap">
          Booking / Tour <p-sortIcon field="booking.bookingNo" />
        </th>
        <th class="min-w-64 text-xs whitespace-nowrap">รายการปรับ</th>
        <th class="min-w-56 text-xs whitespace-nowrap">เหตุผล</th>
        <th pSortableColumn="status" class="w-44 text-xs whitespace-nowrap">
          สถานะ <p-sortIcon field="status" />
        </th>
        <th class="w-28 text-right text-xs whitespace-nowrap">พิจารณา</th>
      </tr>
    </ng-template>

    <ng-template #body let-row let-rowIndex="rowIndex">
      <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/60">
        <td class="py-3.5 text-center text-[13px] text-slate-400 tabular-nums align-top">
          {{ rowIndex + 1 }}
        </td>
        <td class="py-3.5 text-[13px] align-top">
          <p class="m-0 text-sm font-semibold whitespace-nowrap text-slate-800">
            {{ row.adjustmentNo }}
          </p>
          <p class="mt-1 mb-0 text-xs text-slate-500">
            ผู้สร้าง: {{ row.requestedBy.firstName }} {{ row.requestedBy.lastName }}
          </p>
          <p class="mt-1 mb-0 text-xs whitespace-nowrap text-slate-500">
            ส่งเมื่อ {{ row.createdAt | date: 'dd MMM yyyy' }}
          </p>
        </td>
        <td class="py-3.5 text-[13px] align-top">
          @if (row.targetType === 'TOUR' && row.tour) {
            <a [routerLink]="['/tours', row.tourId]" class="text-sm font-semibold whitespace-nowrap text-slate-800 no-underline hover:text-sky-700 hover:underline">{{ row.tour.tourNo }}</a>
            <p class="mt-1 mb-0 text-xs font-semibold text-slate-600">{{ row.tour.tourCode }} · {{ row.tour.tourName }}</p>
            <p class="mt-1 mb-0 max-w-72 text-xs leading-5 text-slate-500">{{ row.tour.country }} · {{ row.tour.travelDate | date: 'dd MMM yyyy' }}{{ row.tour.returnDate ? ' – ' + (row.tour.returnDate | date: 'dd MMM yyyy') : '' }}</p>
          } @else if (row.booking) {
            <a [routerLink]="['/bookings', row.bookingId]" [queryParams]="{ from: row.booking.bookingType === 'GROUP_TOUR' ? 'group-bookings' : 'bookings' }" class="text-sm font-semibold whitespace-nowrap text-slate-800 no-underline hover:text-sky-700 hover:underline">{{ row.booking.bookingNo }}</a>
            <p class="mt-1 mb-0 text-xs font-medium text-slate-600">{{ row.booking.booking_customer.name || '—' }}</p>
            <p class="mt-1 mb-0 max-w-72 text-xs leading-5 text-slate-500">{{ row.booking.tourName || 'ไม่ระบุโปรแกรมทัวร์' }}</p>
          }
        </td>
        <td class="py-3.5 text-[13px] align-top">
          <div class="grid gap-1.5 text-xs">
            <div class="flex items-center justify-between gap-4 text-slate-600">
              <span>เพิ่มค่าทัวร์ทั้งหมด</span>
              <span class="font-semibold whitespace-nowrap tabular-nums">+{{ row.tourPriceDelta | number: '1.2-2' }} บาท</span>
            </div>
            <div class="flex items-center justify-between gap-4 text-slate-600">
              <span>วงเงินค่าใช้จ่าย</span>
              <span class="font-semibold whitespace-nowrap tabular-nums">
                +{{ row.expenseBudgetDelta | number: '1.2-2' }} บาท
              </span>
            </div>
            <div class="flex items-center justify-between gap-4 text-slate-600">
              <span>ยอดเรียกเก็บ</span>
              <span class="font-semibold whitespace-nowrap tabular-nums">
                +{{ row.customerChargeDelta | number: '1.2-2' }} บาท
              </span>
            </div>
            <div class="flex items-center justify-between gap-4 text-slate-600">
              <span>บริษัทสำรอง</span>
              <span class="font-semibold whitespace-nowrap tabular-nums">
                +{{ row.companyAdvanceAmount | number: '1.2-2' }} บาท
              </span>
            </div>
          </div>
        </td>
        <td class="py-3.5 text-[13px] align-top">
          <p class="m-0 line-clamp-3 max-w-60 leading-5 text-slate-600">
            {{ row.reason || '—' }}
          </p>
        </td>
        <td class="py-3.5 align-top"><app-status-badge [status]="row.status" /></td>
        <td class="py-3.5 align-top">
          <div class="flex justify-end gap-1.5">
            @if (row.advanceAttachmentPath) {
              <button
                type="button"
                class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white p-0 text-slate-600 hover:bg-slate-50"
                title="เปิดเอกสารประกอบ"
                (click)="openAttachment(row.advanceAttachmentPath)"
              >
                <lucide-icon name="FileText" [size]="15" />
              </button>
            }
            <button
              type="button"
              class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white p-0 text-slate-600 hover:bg-slate-50"
              title="ดูรายละเอียด"
              (click)="openDetail(row)"
            >
              <lucide-icon name="Eye" [size]="15" />
            </button>
          </div>
        </td>
      </tr>
    </ng-template>

    <ng-template #emptymessage>
      <tr>
        <td colspan="7">
          <div class="empty">
            <b><lucide-icon name="WalletCards" [size]="22" /></b>
            <h3>ไม่มีคำขอปรับวงเงินรออนุมัติ</h3>
            <p>รายการสถานะรอหัวหน้าตรวจสอบจะแสดงที่นี่</p>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</section>

@if (selectedAdjustment(); as adjustment) {
  <div
    class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="closeDetail()"
  >
    <section
      class="my-auto w-full max-w-4xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="financial-adjustment-approval-title"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
            {{ adjustment.adjustmentNo }}
          </p>
          <h2 id="financial-adjustment-approval-title" class="m-0 text-xl font-bold text-slate-800">
            รายละเอียดคำขอปรับวงเงิน
          </h2>
          <div class="mt-2"><app-status-badge [status]="adjustment.status" /></div>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          aria-label="ปิดหน้าต่าง"
          (click)="closeDetail()"
        >
          <lucide-icon name="X" [size]="19" />
        </button>
      </div>

      <div class="max-h-[68vh] overflow-y-auto p-5 sm:p-6">
        <h3 class="m-0 text-base font-bold text-slate-800">{{ adjustment.targetType === 'TOUR' ? 'ข้อมูล Tour' : 'ข้อมูล Booking' }}</h3>
        <dl class="mt-4 grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-3">
          @if (adjustment.targetType === 'TOUR' && adjustment.tour) {
            <div><dt class="text-xs font-semibold text-slate-500">เลขที่ Tour</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.tour.tourNo }}</dd></div>
            <div><dt class="text-xs font-semibold text-slate-500">รหัสทัวร์</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.tour.tourCode }}</dd></div>
            <div><dt class="text-xs font-semibold text-slate-500">ชื่อทัวร์</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.tour.tourName }}</dd></div>
            <div><dt class="text-xs font-semibold text-slate-500">ประเทศ</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.tour.country }}</dd></div>
            <div><dt class="text-xs font-semibold text-slate-500">ราคาทัวร์ต่อคน</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.tour.tourprice | number: '1.2-2' }} บาท</dd></div>
            <div><dt class="text-xs font-semibold text-slate-500">จำนวนคน</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ (adjustment.tour.requiredPax ?? 0) | number }} คน</dd></div>
            <div><dt class="text-xs font-semibold text-slate-500">วันเดินทาง</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.tour.travelDate | date: 'dd MMM yyyy' }}</dd></div>
            <div><dt class="text-xs font-semibold text-slate-500">วันกลับ</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.tour.returnDate ? (adjustment.tour.returnDate | date: 'dd MMM yyyy') : '—' }}</dd></div>
            <div><dt class="text-xs font-semibold text-slate-500">Booking ที่เกี่ยวข้อง</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.booking ? '1 รายการ' : 'ไม่มี' }}</dd></div>
          } @else if (adjustment.booking) {
            <div><dt class="text-xs font-semibold text-slate-500">เลขที่ Booking</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.booking.bookingNo }}</dd></div>
            <div><dt class="text-xs font-semibold text-slate-500">ลูกค้า</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.booking.booking_customer.name }}</dd></div>
            <div><dt class="text-xs font-semibold text-slate-500">โปรแกรมทัวร์</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.booking.tourName }}</dd></div>
            <div><dt class="text-xs font-semibold text-slate-500">วันเดินทาง</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.booking.departureDate | date: 'dd MMM yyyy' }}</dd></div>
            <div><dt class="text-xs font-semibold text-slate-500">จำนวนผู้เดินทาง</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.booking.pax | number }} คน</dd></div>
            <div><dt class="text-xs font-semibold text-slate-500">ยอด Booking</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.booking.totalAmount | number: '1.2-2' }} บาท</dd></div>
          }
        </dl>

        @if (adjustment.customerChargeDelta > 0 && adjustment.booking) {
          <h3 class="mt-6 mb-0 text-base font-bold text-slate-800">ข้อมูล Booking ที่เรียกเก็บเพิ่ม</h3>
          <dl class="mt-4 grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt class="text-xs font-semibold text-slate-500">เลขที่ Booking</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.booking.bookingNo }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">ลูกค้า</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.booking.booking_customer.name }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">โปรแกรมทัวร์</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.booking.tourName }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">จำนวนผู้เดินทาง</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.booking.pax | number }} คน</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">ยอด Booking เดิม</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.booking.totalAmount | number: '1.2-2' }} บาท</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">ยอดที่เรียกเก็บเพิ่ม</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">+{{ adjustment.customerChargeDelta | number: '1.2-2' }} บาท</dd>
            </div>
          </dl>
        }

        <h3 class="mt-6 mb-0 text-base font-bold text-slate-800">ข้อมูลคำขอ</h3>
        <dl class="mt-4 grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-3">
          <div><dt class="text-xs font-semibold text-slate-500">ผู้สร้างคำขอ</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.requestedBy.firstName }} {{ adjustment.requestedBy.lastName }}</dd></div>
          <div><dt class="text-xs font-semibold text-slate-500">เพิ่มค่าทัวร์ทั้งหมด</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.tourPriceDelta | number: '1.2-2' }} บาท</dd></div>
          <div><dt class="text-xs font-semibold text-slate-500">เพิ่มวงเงินค่าใช้จ่าย</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.expenseBudgetDelta | number: '1.2-2' }} บาท</dd></div>
          <div><dt class="text-xs font-semibold text-slate-500">เพิ่มยอดเรียกเก็บ</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.customerChargeDelta | number: '1.2-2' }} บาท</dd></div>
          <div><dt class="text-xs font-semibold text-slate-500">บริษัทสำรองจ่าย</dt><dd class="mt-1 mb-0 font-semibold text-slate-800">{{ adjustment.companyAdvanceAmount | number: '1.2-2' }} บาท</dd></div>
          <div class="sm:col-span-2">
            <dt class="text-xs font-semibold text-slate-500">เอกสารประกอบ</dt>
            <dd class="mt-1 mb-0">
              @if (adjustment.advanceAttachmentPath) {
                <button type="button" class="inline-flex items-center gap-1.5 font-semibold text-sky-700 decoration-sky-300 hover:text-sky-900" title="ดูเอกสารแนบในแท็บใหม่" (click)="openAttachment(adjustment.advanceAttachmentPath)">
                  ดูเอกสารแนบ
                  <lucide-icon name="ExternalLink" [size]="15" />
                </button>
              } @else {
                <span class="text-slate-400">-</span>
              }
            </dd>
          </div>
          <div class="sm:col-span-2 lg:col-span-3"><dt class="text-xs font-semibold text-slate-500">เหตุผล</dt><dd class="mt-1 mb-0 leading-6 text-slate-800">{{ adjustment.reason }}</dd></div>
        </dl>
      </div>

      <div class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
        <button type="button" class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100" (click)="closeDetail()">ปิด</button>
        <button type="button" class="tw-button inline-flex h-11 items-center gap-2 rounded-xl border border-red-200 bg-white px-5 text-[15px] font-semibold text-red-600 hover:bg-red-50" (click)="reject(adjustment)">
          <lucide-icon name="X" [size]="17" /> ปฏิเสธ
        </button>
        <button type="button" class="tw-button inline-flex h-11 items-center gap-2 rounded-xl bg-emerald-600 px-5 text-[15px] font-semibold text-white hover:bg-emerald-700" (click)="approve(adjustment)">
          <lucide-icon name="Check" [size]="17" /> อนุมัติ
        </button>
      </div>
    </section>
  </div>
}
